import { Box, FormLabel, Input, VStack } from '@chakra-ui/react'
import { Select } from 'chakra-react-select'
import { useFormik } from 'formik'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useTranslation } from 'react-i18next'
import { selectCartDetails, selectUserAddress } from 'src/redux/cart/selectors'
import { setCartDetails } from 'src/redux/cart/slice'
import { useGetPostalCodes } from 'src/services/api/queries/queries'
import { CartFormProps } from '../../types'
import NextButton from '../NextButton'
import { FormValues } from './types'
import { getValidationSchema } from './utils'

const AddressDetails: React.FC<CartFormProps> = ({ onNext, userType }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const postalCodes = useGetPostalCodes().data

  const cartDetails = useSelector(selectCartDetails)
  const { postalAddress, streetAddress } = cartDetails[userType]

  const formik = useFormik<FormValues>({
    initialValues: {
      postalCode: postalAddress?.postal_code || '',
      city: postalAddress?.municipality_name || '',
      streetAddress: streetAddress || '',
    },
    validationSchema: getValidationSchema(),
    onSubmit: onNext,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
  })

  const handlePostalCodeChange = (selectedOption: { label: string; value: string }) => {
    const value = selectedOption?.value
    formik.setFieldValue('postalCode', value)

    const selectedPostal = postalCodes?.find((pc) => pc.postal_code === value)
    if (selectedPostal) {
      formik.setFieldValue('city', selectedPostal.municipality_name)
    }
  }

  useEffect(() => {
    const updatedCartDetails = {
      ...cartDetails,
      [userType]: {
        postalAddress: {
          postal_code: formik.values.postalCode,
          municipality_name: formik.values.city,
        },
        streetAddress: formik.values.streetAddress,
      },
    }

    dispatch(setCartDetails(updatedCartDetails))
  }, [formik.values])

  const renderPostalCodeInput = () => (
    <Box w='full'>
      <FormLabel htmlFor='postalCode'>{t('common.postalCode')}</FormLabel>
      <Select
        id='postalCode'
        value={{
          value: formik.values.postalCode,
          label: formik.values.postalCode,
        }}
        onChange={handlePostalCodeChange}
        options={postalCodes?.map((pc) => ({ value: pc.postal_code, label: pc.postal_code }))}
        isClearable={!!formik.values.postalCode}
        isSearchable
      />
    </Box>
  )

  const renderCityInput = () => (
    <Box w='full'>
      <FormLabel htmlFor='city'>{t('common.city')}</FormLabel>
      <Input id='city' value={formik.values.city} isDisabled textAlign='left' width='100%' />
    </Box>
  )

  const renderStreetAddressInput = () => (
    <Box w='full'>
      <FormLabel htmlFor='streetAddress'>{t('common.streetAddress')}</FormLabel>
      <Input
        id='streetAddress'
        value={formik.values.streetAddress}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        textAlign='left'
        width='100%'
      />
    </Box>
  )

  return (
    <Box width={'full'} key={userType}>
      <VStack spacing={4} align='start' width={'full'}>
        {renderPostalCodeInput()}
        {renderCityInput()}
        {renderStreetAddressInput()}
        <NextButton
          onClick={() => formik.handleSubmit()}
          title={t('common.next')}
          isDisabled={!formik.isValid}
        />
      </VStack>
    </Box>
  )
}

export default AddressDetails
